import { useState } from 'react';
import { useAuthStore } from '@app/authStore';
import { Modal, TextField, ActionButton, EyeIcon, EyeOffIcon } from '@shared/ui';
import {
  validateEmail,
  validatePassword,
  validateDisplayName,
  sanitizeEmailInput,
  sanitizeDisplayNameInput,
} from '@shared/lib/validation';

export function AuthModal() {
  const isModalOpen = useAuthStore((s) => s.isModalOpen);
  const mode = useAuthStore((s) => s.mode);
  const isSubmitting = useAuthStore((s) => s.isSubmitting);
  const error = useAuthStore((s) => s.error);
  const closeModal = useAuthStore((s) => s.closeModal);
  const setMode = useAuthStore((s) => s.setMode);
  const login = useAuthStore((s) => s.login);
  const register = useAuthStore((s) => s.register);
  const requestPasswordReset = useAuthStore((s) => s.requestPasswordReset);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});

  function resetFields() {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setShowPassword(false);
    setFieldErrors({});
  }

  function handleClose() {
    resetFields();
    closeModal();
  }

  function handleSwitchMode(next: 'login' | 'register' | 'forgot-password') {
    resetFields();
    setMode(next);
  }

  async function handleSubmit() {
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    const displayNameError = mode === 'register' ? validateDisplayName(displayName) : null;

    if (emailError || passwordError || displayNameError) {
      setFieldErrors({ email: emailError, password: passwordError, displayName: displayNameError });
      return;
    }

    if (mode === 'login') {
      await login(email, password);
    } else {
      await register(email, password, displayName);
    }
  }

  async function handleForgotPasswordSubmit() {
    const emailError = validateEmail(email);
    if (emailError) {
      setFieldErrors({ email: emailError });
      return;
    }
    await requestPasswordReset(email);
  }

  if (mode === 'forgot-password') {
    return (
      <Modal open={isModalOpen} onClose={handleClose} title="Reset Password">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleForgotPasswordSubmit();
          }}
        >
          <p className="mb-4 text-sm text-text-secondary">
            Enter your email and we&apos;ll send you a link to reset your password.
          </p>

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(sanitizeEmailInput(e.target.value))}
            error={fieldErrors.email}
            autoComplete="email"
          />

          {error && <p className="mb-4 text-sm text-status-live">{error}</p>}

          <ActionButton type="submit" variant="accent" disabled={isSubmitting}>
            {isSubmitting ? 'Please wait…' : 'Send reset link'}
          </ActionButton>

          <div className="mt-4 text-center text-sm text-text-secondary">
            <button
              type="button"
              onClick={() => handleSwitchMode('login')}
              className="text-accent hover:underline"
            >
              Back to Sign In
            </button>
          </div>
        </form>
      </Modal>
    );
  }

  if (mode === 'forgot-password-sent') {
    return (
      <Modal open={isModalOpen} onClose={handleClose} title="Check Your Email">
        <p className="mb-5 text-sm text-text-secondary">
          If an account exists for <span className="text-text-primary">{email}</span>, we&apos;ve
          sent a password reset link to it.
        </p>
        <ActionButton variant="accent" onClick={() => handleSwitchMode('login')}>
          Back to Sign In
        </ActionButton>
      </Modal>
    );
  }

  return (
    <Modal
      open={isModalOpen}
      onClose={handleClose}
      title={mode === 'login' ? 'Sign In' : 'Sign Up'}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(sanitizeEmailInput(e.target.value))}
          error={fieldErrors.email}
          autoComplete="email"
        />

        {mode === 'register' && (
          <TextField
            label="Name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(sanitizeDisplayNameInput(e.target.value))}
            error={fieldErrors.displayName}
            autoComplete="name"
          />
        )}

        <TextField
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fieldErrors.password}
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          endAdornment={
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword((v) => !v)}
              className="text-text-secondary hover:text-text-primary"
            >
              {showPassword ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            </button>
          }
        />

        {mode === 'login' && (
          <div className="mb-4 flex justify-end">
            <button
              type="button"
              onClick={() => handleSwitchMode('forgot-password')}
              className="text-xs text-accent hover:underline"
            >
              Forgot password
            </button>
          </div>
        )}

        {error && <p className="mb-4 text-sm text-status-live">{error}</p>}

        <ActionButton type="submit" variant="accent" disabled={isSubmitting}>
          {isSubmitting ? 'Please wait…' : mode === 'login' ? 'Sign In' : 'Sign Up'}
        </ActionButton>

        <div className="mt-4 text-center text-sm text-text-secondary">
          {mode === 'login' ? (
            <>
              Don&apos;t have an account?{' '}
              <button
                type="button"
                onClick={() => handleSwitchMode('register')}
                className="text-accent hover:underline"
              >
                Sign Up
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => handleSwitchMode('login')}
                className="text-accent hover:underline"
              >
                Sign In
              </button>
            </>
          )}
        </div>
      </form>
    </Modal>
  );
}